/**
 * The company's public legal documents and the company details behind them.
 *
 * One module holds every statement the product makes about who runs it and how it treats data,
 * so the marketing footer, the sign-in panel, the customer-facing pages and the legal pages all
 * say the same thing. Tests hold the text to the code: the cookie notice must name every browser
 * storage key the source uses, the subprocessor list must name every provider the platform runs
 * on, and security.txt must point at the disclosure policy published here.
 *
 * These are the operator's own statements, written to the UK GDPR, the Data Protection Act 2018,
 * PECR, the Companies Act trading disclosures, the CCPA/CPRA and CalOPPA. They are not legal
 * advice, and counsel review before production is a launch gate recorded in docs/25.
 */

export interface LegalTable {
  headers: string[];
  rows: string[][];
}

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  table?: LegalTable;
}

export interface LegalDocument {
  slug: string;
  title: string;
  shortTitle: string;
  summary: string;
  version: string;
  effective: string;
  sections: LegalSection[];
}

export const companyDetails = {
  legalName: "Pixel Apocalypse Ltd",
  tradingName: "TidalVenue",
  companyNumber: "14678784",
  registeredIn: "England and Wales",
  registeredOffice: "2 Park Road, Consett, England, DH8 5EA, United Kingdom",
  // Published once issued. Empty renders as "not yet published" rather than as a claim.
  vatNumber: "",
  icoRegistration: "",
  website: "https://tidalvenue.com",
  privacyEmail: "privacy@tidalvenue.com",
  securityEmail: "security@tidalvenue.com",
  legalEmail: "legal@tidalvenue.com",
  supportEmail: "support@tidalvenue.com",
  regulator: {
    name: "Information Commissioner's Office",
    website: "https://ico.org.uk/make-a-complaint/",
    phone: "0303 123 1113",
    address: "Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF, United Kingdom",
  },
} as const;

export const documentVersion = "1.0";
export const effectiveDate = "2026-09-11";

export interface Subprocessor {
  name: string;
  service: string;
  data: string;
  location: string;
  safeguard: string;
}

export const subprocessors: Subprocessor[] = [
  {
    name: "Cloudflare, Inc.",
    service:
      "Edge network, Workers compute, R2 object storage, Queues, Hyperdrive database access and email delivery",
    data: "All service traffic in transit; uploaded documents, exports and email content at rest; queued job payloads",
    location: "United States (global edge network; storage in the region the service assigns)",
    safeguard:
      "EU-US Data Privacy Framework and UK Extension certification; Cloudflare data processing addendum",
  },
  {
    name: "PlanetScale, Inc.",
    service: "Managed PostgreSQL database hosting",
    data: "All structured service data, including account, customer, booking, invoice and message records",
    location: "United States (Amazon Web Services us-east-1, Northern Virginia)",
    safeguard:
      "Data processing addendum incorporating the EU Standard Contractual Clauses and the UK International Data Transfer Addendum, supported by a transfer risk assessment; PlanetScale states that it participates in the EU-US Data Privacy Framework and its UK Extension, and we rely on that certification once its listing is verified",
  },
  {
    name: "Stripe Payments Europe, Ltd. (Ireland) and Stripe, LLC (United States)",
    service: "Subscription billing for the platform and payment processing for workspace invoices",
    data: "Billing contact details, invoice and payment records; card details are entered on Stripe's hosted pages and never reach the platform",
    location: "Ireland and United States",
    safeguard:
      "Our contract is with the Irish entity; the onward transfer to Stripe, LLC relies on its EU-US Data Privacy Framework and UK Extension certification; Stripe data processing agreement",
  },
  {
    name: "Grafana Labs (Raintank, Inc.)",
    service:
      "Operational monitoring: storage of the platform's log lines, dashboards, alert rules, on-call paging and synthetic checks of the service's readiness route",
    data: "Operational telemetry only: request metadata (method, path, status, duration), correlation identifiers, workspace, user, job and delivery identifiers, counts and error wording; never names, email addresses, message content or query strings; kept for 14 days",
    location: "United States (Grafana Cloud, US East region, Virginia)",
    safeguard:
      "Grafana Labs data processing addendum incorporating the EU Standard Contractual Clauses and the UK International Data Transfer Addendum, supported by a transfer risk assessment; Grafana Labs states that it participates in the EU-US Data Privacy Framework and its UK Extension, and we rely on that certification once its listing is verified",
  },
];

export interface BrowserStorageItem {
  name: string;
  kind: "cookie" | "localStorage" | "sessionStorage";
  setBy: string;
  purpose: string;
  duration: string;
  attributes: string;
}

export const browserStorage: BrowserStorageItem[] = [
  {
    name: "__Host-venueops_session",
    kind: "cookie",
    setBy: "TidalVenue",
    purpose: "Keeps you signed in and ties every request to your session",
    duration: "12 hours, or until you sign out",
    attributes: "HttpOnly, Secure, SameSite=Lax, host-only",
  },
  {
    name: "__Host-venueops_csrf",
    kind: "cookie",
    setBy: "TidalVenue",
    purpose: "Proves that a change was requested by this page and not by another site",
    duration: "With the session",
    attributes: "Secure, SameSite=Lax, host-only",
  },
  {
    name: "__Host-venueops_login_challenge",
    kind: "cookie",
    setBy: "TidalVenue",
    purpose: "Carries a two-step sign-in between the password and the authenticator code",
    duration: "Minutes, until the sign-in completes or expires",
    attributes: "HttpOnly, Secure, SameSite=Strict, host-only",
  },
  {
    name: "__cf_bm and cf_clearance",
    kind: "cookie",
    setBy: "Cloudflare",
    purpose: "Distinguish people from automated abuse at the network edge",
    duration: "Up to 30 minutes (__cf_bm); as set by Cloudflare for a completed challenge",
    attributes: "Set by our infrastructure provider; see Cloudflare's cookie documentation",
  },
  {
    name: "venueops-theme and venueops-palette",
    kind: "localStorage",
    setBy: "TidalVenue",
    purpose: "Remember the appearance you chose so the first paint uses it",
    duration: "Until cleared",
    attributes: "No personal data; a theme and palette name",
  },
  {
    name: "venueops-workspace and venueops-venue-scope",
    kind: "localStorage",
    setBy: "TidalVenue",
    purpose: "Remember which half of the building and which venue you were last looking at",
    duration: "Until cleared",
    attributes: "A workspace name and a venue identifier",
  },
  {
    name: "venueops-has-session",
    kind: "localStorage",
    setBy: "TidalVenue",
    purpose: "Lets the page choose the signed-in layout before the server confirms your session",
    duration: "Until you sign out",
    attributes: "A yes or no flag; it grants nothing",
  },
  {
    name: "venueops-public-request:<workspace>",
    kind: "sessionStorage",
    setBy: "TidalVenue",
    purpose:
      "Keeps an in-progress public booking request in this tab so a refresh does not lose it",
    duration: "Until the tab is closed or the request is finished",
    attributes: "A short-lived request token for the booking you started",
  },
];

function formatEffective(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    dateStyle: "long",
    timeZone: "UTC",
  });
}

const c = companyDetails;
const identity = `${c.legalName}, trading as ${c.tradingName}, is a private limited company registered in ${c.registeredIn} (company registration number ${c.companyNumber}) with its registered office at ${c.registeredOffice}.`;

const privacyNotice: LegalDocument = {
  slug: "privacy",
  title: "Privacy notice",
  shortTitle: "Privacy",
  summary:
    "How we handle personal data when we are the controller: website visitors, prospective customers, and the people who hold accounts on the platform. It also explains our role when a venue uses the platform to manage its own customers.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "Who we are and what this notice covers",
      paragraphs: [
        `${identity} We operate the ${c.tradingName} venue and event operations platform.`,
        "This notice explains how we handle personal data when we act as the controller: when you visit our website, ask about the service, hold a staff, owner or operator account on the platform, or correspond with us.",
        "When a venue or organization (our customer, called a workspace in the product) uses the platform to manage its own customers, bookings, invoices and messages, that organization decides why and how that personal data is used and is the controller. We process it on the organization's documented instructions as a processor, which United States state privacy laws call a service provider. If you are a customer of a venue that uses the platform, the venue's own privacy notice applies to that data and requests about it should go to the venue; we help the venue answer you.",
      ],
    },
    {
      heading: "What we collect",
      bullets: [
        "Account data: your name, email address, language preference, an optional picture address and email sign-off, and the workspace roles you hold. Passwords are stored only as a salted hash. Two-step sign-in secrets and recovery codes are stored encrypted or hashed.",
        "Security and usage data: a keyed hash of your IP address and your browser's user agent string for sessions and sign-in challenges, sign-in attempts, and an audit trail of the actions taken in the platform: who did what, when and why.",
        "Billing data for a workspace: the billing contact email address, the plan, invoices and payment status. Card details are entered on Stripe's hosted pages and never reach our systems.",
        "Correspondence: what you send us by email or through the platform's support channels.",
        "Website data: we do not use analytics, advertising or social-media trackers. Our infrastructure provider, Cloudflare, records connection metadata to protect the service from abuse.",
      ],
      paragraphs: [
        "We do not ask for, and do not knowingly collect, special category data, government identifiers, precise location or data about children.",
      ],
    },
    {
      heading: "Why we use it and our lawful bases",
      bullets: [
        "To provide the service you or your organization contracted for (performance of a contract).",
        "To keep the platform secure, prevent abuse and investigate incidents (our legitimate interest, and yours, in a secure service; we do not profile you).",
        "To bill, keep accounts and meet tax, accounting and regulatory obligations (legal obligation).",
        "To send account holders service and security notices (performance of a contract), and to tell prospective customers about the service only where they have asked us to (consent, which can be withdrawn at any time).",
        "To answer requests, complaints and legal process (legal obligation and legitimate interests).",
      ],
      paragraphs: [
        "We do not make decisions about you by automated means that have legal or similarly significant effects.",
      ],
    },
    {
      heading: "Who we share it with",
      bullets: [
        "The subprocessors that host and run the service: Cloudflare, PlanetScale, Stripe and Grafana Labs, which receives operational telemetry only (identifiers and counts, never your content). The current list, with locations and safeguards, is on our Subprocessors page, and changes are announced there at least 30 days in advance.",
        "Professional advisers, insurers and auditors, under confidentiality.",
        "Public authorities, courts and regulators where the law requires it, after we have reviewed the request.",
        "A buyer or successor if the business is transferred, on the terms of this notice.",
      ],
      paragraphs: [
        "We do not sell personal data, and we do not share it for cross-context behavioral advertising.",
      ],
    },
    {
      heading: "International transfers",
      paragraphs: [
        "We are established in the United Kingdom. The service runs on Cloudflare's global network, the database is hosted by PlanetScale on Amazon Web Services in the United States (us-east-1, Northern Virginia), and operational telemetry built from identifiers and counts is held by Grafana Labs in the United States for 14 days. Personal data we hold as controller is therefore transferred from the United Kingdom to the United States.",
        `Where the recipient is certified under the EU-US Data Privacy Framework and its UK Extension (the UK-US data bridge), we rely on that certification. Otherwise we rely on the International Data Transfer Addendum to the EU Standard Contractual Clauses together with a transfer risk assessment. Copies of the relevant terms are available from ${c.privacyEmail}.`,
        "For data we process on behalf of a workspace, transfers are governed by our Data Processing Addendum with that organization.",
      ],
    },
    {
      heading: "How long we keep it",
      bullets: [
        "Account data: while your account exists. When an account is removed, its credentials and two-step secrets are deleted; the audit trail keeps the account identifier.",
        "Sessions and sign-in records: a session lasts twelve hours or until it is revoked; a sign-in challenge lasts minutes; throttling records last the throttling window.",
        "Billing and financial records: six years after the end of the financial year they relate to, as United Kingdom company and tax law requires.",
        "Audit trail: for the life of the workspace and the limitation period that follows. It is append-only by design.",
        "Privacy request exports: thirty days after they are generated.",
        "Backups: rolling backups held by our database provider are overwritten on the provider's schedule; data deleted from the live service leaves backups when they expire.",
      ],
      paragraphs: [
        "The retention schedule for data we process on behalf of workspaces is part of the Data Processing Addendum.",
      ],
    },
    {
      heading: "Your rights",
      paragraphs: [
        "Under the UK GDPR you can ask us for access to your personal data, to correct it, to erase it, to restrict or object to its processing, and to receive the data you gave us in a portable format. Where we rely on consent you can withdraw it at any time without affecting what was done before.",
        `Write to ${c.privacyEmail}. We may ask you to verify your identity. We respond within one month; if a request is complex we may take up to two further months and will tell you why. Requests are free unless they are manifestly unfounded or excessive.`,
        `If you are unhappy with how we handle your data or a request, tell us first: our Complaints page explains the process, and we acknowledge complaints within 30 days. You can also complain to the ${c.regulator.name} (${c.regulator.website}, ${c.regulator.phone}) at any time.`,
      ],
    },
    {
      heading: "Notice for California and other United States residents",
      paragraphs: [
        "This section supplements the notice for residents of California and of other states with comprehensive privacy laws. In the twelve months before the effective date we collected these categories of personal information: identifiers (name, email address, account identifiers and a hashed IP address); customer records (billing contact and invoice details); internet or network activity (sign-in and audit records); professional information (the organization and role you hold); and the content of your correspondence with us. We collect it from you, from the organization that invites you, and from our own systems.",
        "We use it for the business purposes described above, disclose it to the service providers listed on our Subprocessors page, and do not sell it or share it for cross-context behavioral advertising. We do not use or disclose sensitive personal information for purposes other than providing and securing the service. We retain each category for the periods described in this notice.",
        "You may have the right to know what personal information we hold about you, to access it, to correct it, to delete it, to receive it in a portable format, and not to be discriminated against for exercising these rights. Because we do not sell or share personal information, no opt-out is required, and we treat a Global Privacy Control signal as an opt-out request in any case. We do not respond to browser Do Not Track signals because we do not track visitors across other sites.",
        `To exercise a right, email ${c.privacyEmail}. We verify requests by matching them to the account or correspondence they concern. You may use an authorized agent; we ask for proof of the agent's authority and may still confirm the request with you directly. We respond within 45 days and may extend once by a further 45 days with notice.`,
        "For personal information we process on behalf of a venue or organization we act as a service provider; requests should go to that organization, and we help it respond.",
      ],
    },
    {
      heading: "Children",
      paragraphs: [
        `The platform is a business tool and is not directed to children. We do not knowingly collect personal data from anyone under 16, or under 13 in the United States. If you believe a child has given us personal data, contact ${c.privacyEmail} and we will delete it.`,
      ],
    },
    {
      heading: "Security",
      paragraphs: [
        "Data is encrypted in transit and at rest. Staff accounts can enroll two-step sign-in, sensitive actions require a fresh second step, every workspace's data is isolated by row-level security in the database, and administrative actions are recorded in an append-only audit trail. Our vulnerability disclosure policy explains how to report a security concern.",
      ],
    },
    {
      heading: "Changes to this notice",
      paragraphs: [
        `This is version ${documentVersion}, effective ${formatEffective(effectiveDate)}. We post changes here with a new version and effective date, and we notify account holders of material changes by email or in the platform before they take effect.`,
      ],
    },
    {
      heading: "Contact",
      bullets: [
        `Privacy requests and questions: ${c.privacyEmail}`,
        `Security reports: ${c.securityEmail}`,
        `Post: ${c.legalName}, ${c.registeredOffice}`,
      ],
    },
  ],
};

const terms: LegalDocument = {
  slug: "terms",
  title: "Terms of service",
  shortTitle: "Terms",
  summary:
    "The agreement between us and the organization that opens a workspace. A signed order form or master agreement prevails over these terms where they conflict.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "These terms",
      paragraphs: [
        `These terms are between ${c.legalName} ("we", "us") and the organization that opens or accepts ownership of a workspace on the ${c.tradingName} platform ("you"). The person who accepts them confirms that they have authority to bind that organization. A signed order form or master agreement prevails over these terms where the two conflict.`,
      ],
    },
    {
      heading: "The service",
      paragraphs: [
        "The service is a hosted platform for venue, event, booking, finance, messaging and sponsorship operations, provided on the plan you subscribe to. We improve the service continuously and may change or retire features with notice where a change would materially reduce what your plan provides. Sandbox and preview features are provided as they are, for evaluation.",
      ],
    },
    {
      heading: "Accounts and access",
      bullets: [
        "You decide who is invited into your workspace and what role each person holds, and you are responsible for their use of the service.",
        "Credentials must be kept confidential. Owners and administrators must enroll two-step sign-in. Tell us at once if you suspect unauthorized access.",
        "We may suspend an account or a workspace, with notice unless the risk is immediate, to protect the service or other customers.",
      ],
    },
    {
      heading: "Your data",
      bullets: [
        "You own the data you and your customers put into the workspace. You are its controller, you must have a lawful basis to process it, and you must give the people it concerns the notices the law requires.",
        "We process that data only to provide the service and as our Data Processing Addendum permits. We do not use it for advertising, and we do not use it to train models.",
        "You grant us the rights needed to host, copy, transmit, display and back up the data for those purposes.",
      ],
    },
    {
      heading: "Acceptable use",
      bullets: [
        "No unlawful, infringing or harmful content, and no malware.",
        "No attempt to reach another workspace's data, to probe or overload the service, or to bypass its controls. Security research is welcome under our vulnerability disclosure policy.",
        "No unsolicited marketing through the platform: marketing messages need the recipient's consent, an identification of the sender, a working way to opt out and, for United States recipients, a valid postal address.",
        "No reselling of the service and no automated scraping of its interfaces.",
      ],
      paragraphs: [
        "We may remove content or suspend access for a breach, with notice unless the breach creates immediate risk.",
      ],
    },
    {
      heading: "Fees and payment",
      bullets: [
        "Plan prices are published on our website and confirmed when you subscribe. Fees are billed monthly or annually in advance through Stripe.",
        "Fees exclude VAT, sales tax and similar taxes, which we add where the law requires us to collect them. If you are exempt, provide the evidence before the invoice is raised.",
        "If a payment fails we remind you; after the grace period the workspace becomes read-only until payment is made, and a workspace that stays unpaid is closed under the termination section below.",
        "We may change prices with 30 days' notice, effective from your next renewal.",
        "Refunds are given only where the law or an order form requires them.",
      ],
    },
    {
      heading: "Availability and support",
      paragraphs: [
        "We do not offer a percentage uptime guarantee at this stage. We aim to restore the service within 24 hours of a major incident, we schedule maintenance outside your busiest hours where we can and tell you in advance, and we handle security incidents under our published incident process, including notifying you as the Data Processing Addendum requires.",
        `Support is by email at ${c.supportEmail} during United Kingdom business hours, with security reports accepted at any time at ${c.securityEmail}.`,
      ],
    },
    {
      heading: "Confidentiality",
      paragraphs: [
        "Each of us keeps the other's confidential information confidential, uses it only for this agreement, and discloses it only to people who need it and are bound to keep it confidential. This does not apply to information that is public, already known, independently developed or required to be disclosed by law, in which case the party disclosing gives notice where it lawfully can. This section survives for five years after the agreement ends.",
      ],
    },
    {
      heading: "Intellectual property",
      paragraphs: [
        "We own the platform, its software, designs and documentation. You own your data. You may not copy, modify, reverse engineer or create derivative works of the platform except as the law allows. We may use feedback you give us without obligation.",
      ],
    },
    {
      heading: "Warranties and disclaimers",
      paragraphs: [
        "We warrant that the service performs materially as described in the documentation for your plan. Otherwise the service is provided as it is, and we do not warrant that it will be uninterrupted or error-free. You warrant that your use of the service and the data you put into it are lawful.",
      ],
    },
    {
      heading: "Liability",
      bullets: [
        "Nothing in these terms limits liability for death or personal injury caused by negligence, for fraud, or for anything the law does not allow to be limited.",
        "Neither of us is liable for indirect or consequential loss, or for loss of profit, revenue, goodwill or anticipated savings.",
        "Subject to the two points above, our total liability under this agreement in any twelve-month period is limited to the fees you paid for the service in that period.",
        "You indemnify us against claims arising from your data, your content or your unlawful use of the service.",
      ],
    },
    {
      heading: "Term, suspension and termination",
      bullets: [
        "The agreement runs for the plan term and renews automatically unless cancelled before the renewal date. You can cancel at any time, effective at the end of the paid period.",
        "Either of us may terminate for a material breach that is not cured within 30 days of notice. We may terminate immediately for non-payment after the read-only period, or for a breach that creates immediate risk.",
        "For 30 days after termination you can export your data. We then delete it within 90 days, except backups, which expire on their own schedule, and records we must keep by law.",
      ],
    },
    {
      heading: "Changes to these terms",
      paragraphs: [
        "We may update these terms. We give at least 30 days' notice of a material change by email to workspace owners and in the platform. If you do not accept a change you may terminate before it takes effect; continued use after that date is acceptance.",
      ],
    },
    {
      heading: "Law, disputes and notices",
      paragraphs: [
        "These terms are governed by the law of England and Wales, and the courts of England and Wales have exclusive jurisdiction, except that either of us may seek urgent relief in any competent court. Where a customer is a public body whose law requires particular terms, those terms are set out in the order form and prevail.",
        `Notices to us go to ${c.legalEmail} or to our registered office. Notices to you go to the workspace owner's email address.`,
      ],
    },
    {
      heading: "General",
      bullets: [
        "This agreement is the entire agreement between us about the service.",
        "Neither of us may assign it without consent, except to a successor of the whole business on notice.",
        "Neither of us is liable for delay caused by events outside reasonable control, other than payment obligations.",
        "If a provision is unenforceable the rest stands, and a delay in enforcing a right does not waive it.",
        "No one other than the parties may enforce these terms.",
      ],
    },
  ],
};

const dataProcessing: LegalDocument = {
  slug: "data-processing",
  title: "Data processing addendum",
  shortTitle: "DPA",
  summary:
    "The terms under which we process personal data on behalf of a workspace. This page summarizes the addendum; the signed addendum, which incorporates the UK Addendum and the EU Standard Contractual Clauses, is available on request.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "Purpose and status",
      paragraphs: [
        `The Data Processing Addendum forms part of the Terms of service for every workspace. This page summarizes it. To receive the full addendum for signature, including the UK International Data Transfer Addendum and the EU Standard Contractual Clauses where they apply, write to ${c.legalEmail}. Where this summary and the signed addendum differ, the signed addendum prevails.`,
      ],
    },
    {
      heading: "Roles",
      paragraphs: [
        "You are the controller (or business) of the personal data in your workspace. We are your processor (or service provider). We engage the subprocessors listed on our Subprocessors page, each under written terms no less protective than the addendum.",
      ],
    },
    {
      heading: "Details of the processing",
      bullets: [
        "Subject matter and purpose: hosting and operating the platform so you can manage venues, events, bookings, customers, invoices, payments, messages, documents and sponsorships.",
        "Duration: the term of the agreement plus the export and deletion period after it ends.",
        "Data subjects: your staff, your customers and their contacts, people who submit public booking requests, portal users, suppliers and sponsors.",
        "Categories of data: names, contact details, organization and role, booking and event details, quotes, invoices and payment status, messages and attachments, documents you upload, preferences and consents, and the audit records of actions in the workspace. The platform is not designed for special category data, and you agree not to put it into free-text fields where it can be avoided.",
      ],
    },
    {
      heading: "Our obligations",
      bullets: [
        "Process personal data only on your documented instructions, which include the agreement and your configuration and use of the platform, unless the law requires otherwise, in which case we tell you first where we lawfully can.",
        "Ensure that everyone we authorize to process the data is bound by confidentiality.",
        "Apply the security measures summarized below and keep them under review.",
        "Give 30 days' notice before adding or replacing a subprocessor, on the Subprocessors page and by email to workspace owners; you may object on reasonable grounds, and if we cannot resolve the objection you may terminate the affected service without penalty.",
        "Help you respond to requests from data subjects. The platform records requests, verifies identity, generates a portable export, minimizes direct identifiers on erasure, and supports legal holds; what the automated passes leave for human review is set out in the runbook we share with you.",
        "Help you meet your security, breach notification and impact assessment obligations, taking into account the nature of the processing and the information available to us.",
        "At the end of the agreement, return the data through the export tools for 30 days and then delete it within 90 days, except backups, which expire on their own schedule, and records we must keep by law.",
        "Make available the information you need to demonstrate compliance: an annual written security questionnaire, our current policies and test evidence, and, once a year or after an incident, an audit at your cost on reasonable notice, conducted so as not to disturb other customers.",
      ],
    },
    {
      heading: "Personal data breach",
      paragraphs: [
        "We notify you without undue delay after becoming aware of a personal data breach affecting your data, and in any event within 48 hours, with what we know about the nature of the breach, the categories and approximate numbers of data subjects and records concerned, the likely consequences, the measures taken or proposed, and a point of contact. We update the notice as we learn more. We do not notify your customers or a regulator on your behalf unless you instruct us to or the law requires it of us.",
      ],
    },
    {
      heading: "International transfers",
      paragraphs: [
        "Personal data is stored in the United States and passes through Cloudflare's global network. For transfers from the United Kingdom we rely on the UK-US data bridge where the importer is certified, and otherwise on the International Data Transfer Addendum to the EU Standard Contractual Clauses. For transfers from the European Economic Area we rely on the EU-US Data Privacy Framework where the importer is certified, and otherwise on the EU Standard Contractual Clauses (modules two and three). Transfer risk assessments are available on request.",
      ],
    },
    {
      heading: "United States service-provider terms",
      paragraphs: [
        "Where the California Consumer Privacy Act or another United States state privacy law applies to personal information in your workspace, we act as your service provider or processor and we:",
      ],
      bullets: [
        "do not sell or share the personal information;",
        "do not retain, use or disclose it for any purpose other than the business purposes in the agreement, or outside the direct business relationship with you;",
        "do not combine it with personal information we receive from anyone else, except as the law permits;",
        "comply with the applicable law and give the same level of privacy protection it requires;",
        "tell you if we can no longer meet these obligations, and allow you to take reasonable steps to stop and remediate unauthorized use;",
        "flow the same obligations down to our subprocessors.",
      ],
    },
    {
      heading: "Security measures (summary of Annex II)",
      bullets: [
        "Encryption in transit with TLS 1.2 or later and HTTP Strict Transport Security; encryption at rest by our hosting providers.",
        "Tenant isolation enforced by PostgreSQL row-level security and re-checked in the application on every request.",
        "Password hashing with Argon2id, sign-in throttling, two-step sign-in, a fresh second step for sensitive actions, twelve-hour sessions and user-controlled session revocation.",
        "Least-privilege access for our staff, individual accounts, and audited, time-limited support access that a workspace owner grants.",
        "An append-only audit trail of security-relevant actions.",
        "Malware scanning of uploads that fails closed, and content security policies that stop injected scripts.",
        "Secrets held in the platform's secret store, never in source code, with an automated check that refuses credentials in the repository.",
        "Automated security tests on every change, a personal-data inventory test that fails when a new column holding personal data is not classified, and a vulnerability disclosure policy.",
        "Provider-managed backups, a written incident response process and a data-subject request runbook.",
      ],
    },
    {
      heading: "Liability and precedence",
      paragraphs: [
        "The liability provisions of the Terms of service apply to the addendum. Where the addendum, the UK Addendum or the Standard Contractual Clauses conflict with the Terms, the transfer terms prevail, then the addendum, then the Terms.",
      ],
    },
  ],
};

const subprocessorList: LegalDocument = {
  slug: "subprocessors",
  title: "Subprocessors",
  shortTitle: "Subprocessors",
  summary:
    "The third parties that process personal data on our behalf to run the platform, where they process it, and the safeguard for each transfer.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "Current subprocessors",
      paragraphs: [
        "We use a deliberately short list of providers, and each one is bound by written terms that are no less protective than our Data Processing Addendum. We do not use analytics, advertising or support tools that receive customer data.",
      ],
      table: {
        headers: ["Provider", "Service", "Data", "Location", "Safeguard"],
        rows: subprocessors.map((item) => [
          item.name,
          item.service,
          item.data,
          item.location,
          item.safeguard,
        ]),
      },
    },
    {
      heading: "Our own access",
      paragraphs: [
        "Our staff administer the platform from the United Kingdom. Support access to a workspace is granted by a workspace owner, limited in time and recorded in the audit trail, and we do not use third-party support or analytics tools that receive customer data. The monitoring service we use, Grafana Labs, receives operational telemetry built from identifiers and counts and never receives customer content.",
      ],
    },
    {
      heading: "Changes",
      paragraphs: [
        "We announce a new or replacement subprocessor on this page and by email to workspace owners at least 30 days before it starts processing personal data. A workspace may object on reasonable grounds within that period; if the objection cannot be resolved, the workspace may terminate the affected service without penalty, as the Data Processing Addendum provides.",
        `This list was last reviewed on ${formatEffective(effectiveDate)}.`,
      ],
    },
  ],
};

const cookies: LegalDocument = {
  slug: "cookies",
  title: "Cookies and browser storage",
  shortTitle: "Cookies",
  summary:
    "Every cookie and every piece of browser storage the platform uses, what each one is for, and why there is no cookie banner: all of it is strictly necessary and none of it tracks you.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "What we use",
      paragraphs: [
        "The platform sets three cookies of its own, all of which exist to keep you signed in safely, and it keeps a handful of preferences in your browser's storage so the page can draw itself the way you left it. Our infrastructure provider sets its own cookies to tell people apart from automated abuse. Nothing here is used for advertising, analytics or tracking across other websites.",
      ],
      table: {
        headers: ["Name", "Type", "Set by", "Purpose", "Duration", "Details"],
        rows: browserStorage.map((item) => [
          item.name,
          item.kind,
          item.setBy,
          item.purpose,
          item.duration,
          item.attributes,
        ]),
      },
    },
    {
      heading: "Why there is no cookie banner",
      paragraphs: [
        "United Kingdom law (the Privacy and Electronic Communications Regulations, regulation 6) requires consent for storing or reading information on your device unless it is strictly necessary to provide a service you asked for. Every item above is strictly necessary for that purpose: the session and forgery-protection cookies are what make signing in work, the sign-in challenge cookie is what makes two-step sign-in work, the preferences are yours, and the provider cookies are what keep the service reachable under attack. We therefore do not ask for consent, and we will ask before adding anything that is not strictly necessary.",
      ],
    },
    {
      heading: "Managing cookies and storage",
      paragraphs: [
        "You can delete cookies and site data through your browser's settings at any time. Deleting the session cookie signs you out; deleting the stored preferences returns the appearance to the default. Blocking cookies entirely prevents signing in.",
      ],
    },
    {
      heading: "Workspaces on their own domain",
      paragraphs: [
        "A workspace can serve its public booking page and customer portal on its own domain. The same cookies and storage apply there, scoped to that domain, and the workspace's own privacy notice describes what the workspace does with the details you enter.",
      ],
    },
  ],
};

const accessibility: LegalDocument = {
  slug: "accessibility",
  title: "Accessibility statement",
  shortTitle: "Accessibility",
  summary:
    "Our commitment to WCAG 2.2 level AA, what has been done, what we know still falls short, and how to tell us about a barrier.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "Our commitment",
      paragraphs: [
        `${c.legalName} wants everyone to be able to use the ${c.tradingName} platform, its public booking pages and its customer portal. Our target is conformance with the Web Content Accessibility Guidelines (WCAG) 2.2 at level AA, which also satisfies the WCAG 2.1 AA standard referenced by the Americans with Disabilities Act Title II rule and Section 508.`,
      ],
    },
    {
      heading: "Conformance status",
      paragraphs: [
        "The platform is partially conformant with WCAG 2.2 level AA: the interface is built to the design standards we publish internally, which fix minimum contrast, focus visibility, target size, keyboard operation and reduced-motion behavior, and those rules are checked in our automated tests. An independent accessibility audit has not yet been completed, and until it is we describe the platform as partially conformant rather than conformant.",
      ],
    },
    {
      heading: "What we have done",
      bullets: [
        "Text and interface components meet the 4.5:1 and 3:1 contrast minimums in every color theme, in light and dark schemes.",
        "Every control can be reached and operated with a keyboard, and the focused control is always visible.",
        "Pages, tables, dialogs and forms use semantic HTML and ARIA so that assistive technology can name and navigate them.",
        "Layouts respond to text scaling and to narrow screens without loss of content, and animation respects the reduced-motion preference.",
        "Every page carries a descriptive title and a skip target for the main content.",
      ],
    },
    {
      heading: "Known limitations",
      bullets: [
        "On a phone, the calendar scrolls the page rather than fitting the screen; the content is all reachable, but it takes more scrolling than on a larger screen.",
        "Documents that a venue uploads and shares through the portal are outside our control and may not be accessible.",
        "An independent audit, including testing with screen readers across the full application, is scheduled as part of our launch gates and may identify further issues, which will be listed here.",
      ],
    },
    {
      heading: "Feedback and enforcement",
      paragraphs: [
        `If you find a barrier, or need content in another format, email ${c.supportEmail} with the page and what happened. We reply within ten business days and treat access barriers as defects to be fixed. Public-sector customers in the United States can request an Accessibility Conformance Report (VPAT 2.5 format) once the independent audit is complete.`,
      ],
    },
    {
      heading: "Preparation of this statement",
      paragraphs: [
        `This statement was prepared on ${formatEffective(effectiveDate)} on the basis of a self-assessment against WCAG 2.2 supported by the automated checks in our test suite. It will be reviewed when the independent audit reports and at least annually.`,
      ],
    },
  ],
};

const vulnerabilityDisclosure: LegalDocument = {
  slug: "vulnerability-disclosure",
  title: "Vulnerability disclosure policy",
  shortTitle: "Security",
  summary:
    "How to report a security vulnerability to us, what we ask of researchers, what you can expect from us, and the safe harbor we offer for good-faith research.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "Scope",
      paragraphs: [
        `This policy covers the ${c.tradingName} platform at tidalvenue.com and its subdomains, including the sandbox environment, and the software behind them. Services run by other companies, including our hosting and payment providers, are out of scope and should be reported to those companies under their own policies.`,
      ],
    },
    {
      heading: "How to report",
      paragraphs: [
        `Email ${c.securityEmail} with what you found, the steps to reproduce it, the impact you believe it has, and how we can reach you. The same address is published in our security.txt file at /.well-known/security.txt. Please report as soon as you find a problem and keep the details between us until we have fixed it.`,
      ],
    },
    {
      heading: "What we ask",
      bullets: [
        "Test only against accounts and workspaces you own or are authorized to use; never against another customer's data.",
        "Stop and tell us as soon as you encounter personal data, and do not copy, keep or share it beyond what is needed to show the problem.",
        "Do not degrade the service: no denial of service, no automated scanning at volume, no spam.",
        "No social engineering of our staff or customers, and no physical attacks.",
        "Do not publish the vulnerability before we have fixed it or 90 days have passed, whichever is sooner, unless we agree otherwise.",
      ],
    },
    {
      heading: "What you can expect",
      bullets: [
        "An acknowledgment within three business days and an initial assessment within ten.",
        "Fix targets by severity: critical issues within seven days, high within 30, medium within 90, and lower severities in the ordinary release cycle.",
        "Updates as we work, and confirmation when the fix ships. With your permission we credit you.",
        "We do not currently pay bounties.",
      ],
    },
    {
      heading: "Safe harbor",
      paragraphs: [
        "If you make a good-faith effort to follow this policy, we consider your research authorized, we will not pursue or support legal action against you under the Computer Misuse Act 1990, the Computer Fraud and Abuse Act or similar laws for that research, and we will work with you to understand and resolve the issue quickly. If a third party starts legal action against you for research that complied with this policy, we will make it known that your actions were authorized.",
      ],
    },
    {
      heading: "Out of scope",
      bullets: [
        "Reports of missing best practices without a demonstrated impact, such as header configuration or version disclosure alone.",
        "Rate limiting, password policy or email security findings without a working exploit.",
        "Issues in third-party services we use, which belong with those providers.",
        "Vulnerabilities that require a compromised device or physical access.",
      ],
    },
  ],
};

const complaints: LegalDocument = {
  slug: "complaints",
  title: "Complaints and contact",
  shortTitle: "Complaints",
  summary:
    "How to complain about the way we have handled your personal data or the service, what happens next, and how to escalate to the regulator.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "How to complain",
      bullets: [
        `About personal data: ${c.privacyEmail}`,
        `About the service or an account: ${c.supportEmail}`,
        `About a contract or a legal notice: ${c.legalEmail}`,
        `By post: ${c.legalName}, ${c.registeredOffice}`,
      ],
      paragraphs: [
        "Tell us what happened, when, which workspace or account it concerns, and what you would like us to do. Complaints are free to make.",
      ],
    },
    {
      heading: "What happens next",
      bullets: [
        "We acknowledge every complaint within 30 days of receiving it, and usually much sooner.",
        "Someone independent of the original decision investigates, and we tell you the outcome, the reasons and anything we have changed as a result. We aim to do that within 30 days of the acknowledgment.",
        "If you are not satisfied you can ask for the complaint to be reviewed by a director.",
      ],
    },
    {
      heading: "If you remain dissatisfied",
      paragraphs: [
        `You can complain to the ${c.regulator.name} at any time, and you do not have to complain to us first: ${c.regulator.website}, ${c.regulator.phone}, or by post to ${c.regulator.address}. If you are in another country you may also complain to your own data protection authority.`,
      ],
    },
    {
      heading: "Customers of a venue",
      paragraphs: [
        "If your complaint is about what a venue or organization has done with your data through the platform, that organization is the controller and should hear from you first; its details are on the booking page or portal you used. We help it respond, and you can still write to us if you believe we have done something wrong ourselves.",
      ],
    },
  ],
};

const company: LegalDocument = {
  slug: "company",
  title: "Company information",
  shortTitle: "Company",
  summary:
    "The statutory details of the company that operates the platform, published as the Companies Act 2006 and the Electronic Commerce Regulations 2002 require.",
  version: documentVersion,
  effective: effectiveDate,
  sections: [
    {
      heading: "Operator",
      bullets: [
        `Legal name: ${c.legalName}`,
        `Trading name: ${c.tradingName}`,
        `Registered in: ${c.registeredIn}`,
        `Company registration number: ${c.companyNumber}`,
        `Registered office: ${c.registeredOffice}`,
        `VAT registration number: ${c.vatNumber || "not yet published"}`,
        `Data protection registration with the ${c.regulator.name}: ${c.icoRegistration || "not yet published"}`,
        `Website: ${c.website}`,
      ],
    },
    {
      heading: "Contact",
      bullets: [
        `General and support: ${c.supportEmail}`,
        `Privacy: ${c.privacyEmail}`,
        `Security: ${c.securityEmail}`,
        `Legal notices: ${c.legalEmail}`,
      ],
    },
    {
      heading: "Payments and regulation",
      paragraphs: [
        "We are not a bank or a regulated payment institution. Subscription payments and payments to venues through the platform are processed by Stripe, which holds the relevant authorizations; card details are entered on Stripe's pages and never reach us.",
      ],
    },
  ],
};

export const legalDocuments: LegalDocument[] = [
  privacyNotice,
  terms,
  dataProcessing,
  subprocessorList,
  cookies,
  accessibility,
  vulnerabilityDisclosure,
  complaints,
  company,
];

export function findLegalDocument(slug: string): LegalDocument | undefined {
  return legalDocuments.find((item) => item.slug === slug);
}

/** Every word a document carries, for tests that hold the text to the code. */
export function legalDocumentText(item: LegalDocument): string {
  const parts = [item.title, item.summary];
  for (const section of item.sections) {
    parts.push(section.heading, ...(section.paragraphs ?? []), ...(section.bullets ?? []));
    if (section.table) parts.push(...section.table.headers, ...section.table.rows.flat());
  }
  return parts.join("\n");
}

export { formatEffective };
